/**
 * Grand Parkway WebMap Configuration
 *
 * This file contains all map settings, layer definitions, and styles.
 * Update tileset IDs and access token after uploading to Mapbox.
 */

// Mapbox Configuration
export const MAPBOX_CONFIG = {
  // Get this from your Mapbox account page
  accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.YOUR_TOKEN_HERE',

  // Base map style - satellite view
  style: 'mapbox://styles/mapbox/satellite-v9',

  // Initial map view (center of Grand Parkway area)
  center: [-95.691, 29.428],
  zoom: 11,

  // Zoom constraints
  minZoom: 9,
  maxZoom: 16,  // Mapbox limit for uploaded tilesets

  // Map bounds (prevents panning too far away)
  maxBounds: [
    [-97.3, 28.5],  // Southwest corner [lng, lat]
    [-94.0, 30.3]   // Northeast corner [lng, lat]
  ],

  // Initial fit bounds
  bounds: [
    [-96.987, 28.85],  // Southwest
    [-94.396, 30.006]  // Northeast
  ]
};

// GeoJSON Data Files
// Loading parcels as GeoJSON instead of vector tiles to preserve 100% of data
// Vector tiles were dropping 87% of features due to density constraints
export const GEOJSON_FILES = {
  brazoria1000ft: '/data/brazoria1000feetparcels_1.geojson',
  brazoriaHalfMile: '/data/brazoriahalfmileparcels_2.geojson',
  fortBend1000ft: '/data/fortbend1000feetparcels_3.geojson',
  fortBendHalfMile: '/data/fortbendhalfmileparcels_4.geojson',
  galveston1000ft: '/data/galveston1000feetparcels_5.geojson',
  galvestonHalfMile: '/data/galvestonhalfmileparcels_6.geojson'
};

// Layer Definitions - Using direct GeoJSON loading (preserves 100% of parcel data)
export const LAYERS = [
  {
    id: 'brazoria-1000ft',
    name: 'Brazoria 0-1,000 ft',
    county: 'Brazoria',
    distance: '0-1,000 feet',
    geojsonFile: GEOJSON_FILES.brazoria1000ft,
    visible: false,
    style: {
      fillColor: '#e31a1c',
      fillOpacity: 0.5,
      lineColor: '#e31a1c',
      lineWidth: 1
    }
  },
  {
    id: 'brazoria-halfmile',
    name: 'Brazoria 1,000 ft - 0.5 mi',
    county: 'Brazoria',
    distance: '1,000 ft - 0.5 miles',
    geojsonFile: GEOJSON_FILES.brazoriaHalfMile,
    visible: false,
    style: {
      fillColor: '#fd8d3c',
      fillOpacity: 0.5,
      lineColor: '#fd8d3c',
      lineWidth: 1
    }
  },
  {
    id: 'fortbend-1000ft',
    name: 'Fort Bend 0-1,000 ft',
    county: 'Fort Bend',
    distance: '0-1,000 feet',
    geojsonFile: GEOJSON_FILES.fortBend1000ft,
    visible: false,
    style: {
      fillColor: '#2b83ba',
      fillOpacity: 0.5,
      lineColor: '#2b83ba',
      lineWidth: 1
    }
  },
  {
    id: 'fortbend-halfmile',
    name: 'Fort Bend 1,000 ft - 0.5 mi',
    county: 'Fort Bend',
    distance: '1,000 ft - 0.5 miles',
    geojsonFile: GEOJSON_FILES.fortBendHalfMile,
    visible: false,
    style: {
      fillColor: '#abdda4',
      fillOpacity: 0.5,
      lineColor: '#abdda4',
      lineWidth: 1
    }
  },
  {
    id: 'galveston-1000ft',
    name: 'Galveston 0-1,000 ft',
    county: 'Galveston',
    distance: '0-1,000 feet',
    geojsonFile: GEOJSON_FILES.galveston1000ft,
    visible: false,
    style: {
      fillColor: '#d7191c',
      fillOpacity: 0.5,
      lineColor: '#d7191c',
      lineWidth: 1
    }
  },
  {
    id: 'galveston-halfmile',
    name: 'Galveston 1,000 ft - 0.5 mi',
    county: 'Galveston',
    distance: '1,000 ft - 0.5 miles',
    geojsonFile: GEOJSON_FILES.galvestonHalfMile,
    visible: false,
    style: {
      fillColor: '#fdae61',
      fillOpacity: 0.5,
      lineColor: '#fdae61',
      lineWidth: 1
    }
  }
];

// Subdivision and Development Layers
// Note: These are now rendered as rectangle polygons sized by Units Planned
export const SUBDIVISION_LAYERS = [
  {
    id: 'future-subdivisions',
    name: 'Future Subdivisions',
    type: 'subdivisions',
    dataFile: new URL('../data/future-subdivisions.geojson', import.meta.url).href,
    visible: false,
    style: {
      // Polygon fill
      fillColor: '#ff8c00',  // Dark orange
      fillOpacity: 0.5,
      // Polygon border
      lineColor: '#ffffff',
      lineWidth: 2,
      // Legacy circle properties (for fallback)
      circleColor: '#ff8c00',
      circleRadius: 7,
      circleStrokeColor: '#ffffff',
      circleStrokeWidth: 2,
      circleOpacity: 0.85
    }
  },
  {
    id: 'upcoming-projects',
    name: 'Upcoming Projects',
    type: 'projects',
    dataFile: new URL('../data/upcoming-projects.geojson', import.meta.url).href,
    visible: false,
    style: {
      // Polygon fill
      fillColor: '#32cd32',  // Lime green
      fillOpacity: 0.5,
      // Polygon border
      lineColor: '#ffffff',
      lineWidth: 2,
      // Legacy circle properties (for fallback)
      circleColor: '#32cd32',
      circleRadius: 7,
      circleStrokeColor: '#ffffff',
      circleStrokeWidth: 2,
      circleOpacity: 0.85
    }
  }
];

// Group layers by county for UI
export const LAYER_GROUPS = [
  {
    county: 'Fort Bend',
    layers: LAYERS.filter(l => l.county === 'Fort Bend')
  },
  {
    county: 'Galveston',
    layers: LAYERS.filter(l => l.county === 'Galveston')
  },
  {
    county: 'Brazoria',
    layers: LAYERS.filter(l => l.county === 'Brazoria')
  }
];

// Subdivision layer group for UI
export const SUBDIVISION_LAYER_GROUP = {
  name: 'Future Developments',
  layers: SUBDIVISION_LAYERS
};

// Popup Configuration
export const POPUP_CONFIG = {
  maxWidth: '400px',
  className: 'parcel-popup',
  closeButton: true,
  closeOnClick: false
};

// Property display configuration
// Controls which properties to show in popups and their labels
export const PROPERTY_LABELS = {
  // Brazoria County
  'file_as_na': 'Owner Name',
  'situs_disp': 'Property Address',
  'ACREAGE': 'Acreage',
  'owner_name': 'Owner',

  // Fort Bend County
  'Owner_Name': 'Owner Name',
  'Situs': 'Property Address',
  'Total_Valu': 'Total Value',
  'Land_Value': 'Land Value',

  // Galveston County
  'NAME': 'Owner Name',
  'SITUS': 'Property Address',
  'ACRES': 'Acreage',
  'VAL25TOT': 'Total Value (2025)',
  'VAL25LAND': 'Land Value (2025)',
  'VAL25IMP': 'Improvement Value (2025)',

  // Common fields
  'GEOID': 'Parcel ID',
  'PID': 'Property ID',
  'LEGAL': 'Legal Description',
  'LANDUSE': 'Land Use',
  'NBHD': 'Neighborhood',

  // Subdivision/Development fields
  'Name': 'Development Name',
  'Section': 'Section',
  'Builder': 'Builder',
  'Status': 'Status',
  'Product Style': 'Product Style',
  'Lot Width': 'Lot Width',
  'Lot Size': 'Lot Size',
  'Unit Size Min': 'Min Unit Size',
  'Unit Size Max': 'Max Unit Size',
  'Min Unit Size': 'Min Unit Size',
  'Max Unit Size': 'Max Unit Size',
  'Price Min': 'Min Price',
  'Price Max': 'Max Price',
  'Min Price': 'Min Price',
  'Max Price': 'Max Price',
  'Units Planned': 'Units Planned',
  'Units Closed': 'Units Sold',
  'Total Planned': 'Total Planned',
  'Total Sold': 'Total Sold',
  'Sales Rate': 'Sales Rate',
  'City': 'City',
  'State': 'State',
  'Zip Code': 'Zip Code',
  'Open Date': 'Expected Opening',
  'Sold Out Date': 'Expected Sellout',
  'Distance_Miles': 'Distance to Route'
};

// Attribution
export const ATTRIBUTION = {
  map: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  data: 'Grand Parkway Project | © Texas DOT',
  app: 'Powered by <a href="https://www.mapbox.com/mapbox-gljs">Mapbox GL JS</a>'
};
